/**
 * Ticket photos: the "Add photos" control for a new ticket or a reply, and the thumbnail grid that
 * shows a ticket's or comment's photos with a full-size viewer.
 *
 * Photos upload as soon as they are picked (POST api/tickets/attachments) and the ticket or comment
 * then names their ids, so sending never waits on an upload that already failed. The links the server
 * returns are signed and short-lived; the detail pages refetch to renew them.
 */

import { useEffect, useRef, useState } from 'react'
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material'
import { AddPhotoAlternate, BrokenImage, ChevronLeft, ChevronRight, Close, ErrorOutline } from '@mui/icons-material'
import type { TicketAttachmentDto } from '@api/types/tickets'
import { MAX_TICKET_PHOTOS, TICKET_PHOTO_ACCEPT } from './ticketPhotos'
import type { PickedTicketPhoto } from './useTicketPhotos'
import { useMobileDialogProps } from '@components/common/useMobileDialogProps'

interface TicketPhotoPickerProps {
  photos: PickedTicketPhoto[]
  onAdd: (files: File[]) => number
  onRemove: (key: string) => void
  disabled?: boolean
}

/** "Add photos" button plus the picked photos with their upload state. */
export function TicketPhotoPicker({ photos, onAdd, onRemove, disabled }: TicketPhotoPickerProps) {
  const input = useRef<HTMLInputElement>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const active = photos.filter((p) => p.status !== 'failed').length
  const full = active >= MAX_TICKET_PHOTOS

  const handleFiles = (list: FileList | null) => {
    if (!list || list.length === 0) return
    const skipped = onAdd(Array.from(list))
    setNotice(skipped > 0 ? `Only ${MAX_TICKET_PHOTOS} photos can be attached; ${skipped} left out.` : null)
    if (input.current) input.current.value = ''
  }

  return (
    <Box>
      <input
        ref={input}
        type="file"
        accept={TICKET_PHOTO_ACCEPT}
        multiple
        hidden
        data-testid="ticket-photo-input"
        onChange={(e) => handleFiles(e.target.files)}
      />
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Button
          size="small"
          variant="outlined"
          startIcon={<AddPhotoAlternate />}
          onClick={() => input.current?.click()}
          disabled={disabled || full}
        >
          Add photos
        </Button>
        <Typography variant="caption" color="text.secondary">
          {active}/{MAX_TICKET_PHOTOS} · only people who can see this ticket can see them
        </Typography>
      </Box>

      {notice && (
        <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 0.5 }}>
          {notice}
        </Typography>
      )}

      {photos.length > 0 && (
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
          {photos.map((photo) => (
            <Box
              key={photo.key}
              sx={{
                position: 'relative',
                width: 72,
                height: 72,
                borderRadius: 1,
                overflow: 'hidden',
                bgcolor: 'action.hover',
                border: photo.status === 'failed' ? '2px solid' : 'none',
                borderColor: 'error.main',
              }}
            >
              {photo.previewUrl && photo.status !== 'failed' && (
                <Box
                  component="img"
                  src={photo.previewUrl}
                  alt=""
                  sx={{ width: '100%', height: '100%', objectFit: 'cover', opacity: photo.status === 'uploading' ? 0.5 : 1 }}
                />
              )}
              {photo.status === 'uploading' && (
                <CircularProgress size={24} sx={{ position: 'absolute', top: 24, left: 24 }} />
              )}
              {photo.status === 'failed' && (
                <Tooltip title={photo.error ?? 'Not added'}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                    <ErrorOutline color="error" />
                  </Box>
                </Tooltip>
              )}
              {/* A 40px tap target (the old one was ~18px, a near-miss on a phone) with a small
                  dark disc inside it so it does not cover the thumbnail. */}
              <IconButton
                aria-label="Remove photo"
                onClick={() => onRemove(photo.key)}
                sx={{
                  position: 'absolute',
                  top: -4,
                  right: -4,
                  width: 40,
                  height: 40,
                  p: 0,
                  color: '#fff',
                  '& .remove-disc': { bgcolor: 'rgba(0,0,0,0.6)', borderRadius: '50%', width: 24, height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center' },
                  '&:hover .remove-disc': { bgcolor: 'rgba(0,0,0,0.8)' },
                }}
              >
                <Box component="span" className="remove-disc">
                  <Close sx={{ fontSize: 16 }} />
                </Box>
              </IconButton>
            </Box>
          ))}
        </Box>
      )}

      {photos
        .filter((p) => p.status === 'failed')
        .map((p) => (
          <Typography key={p.key} variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
            {p.error}
          </Typography>
        ))}
    </Box>
  )
}

interface TicketPhotoGridProps {
  attachments: TicketAttachmentDto[] | null | undefined
  size?: number
}

/** A ticket's or comment's photos as thumbnails; click one for the full-size photo. */
export function TicketPhotoGrid({ attachments, size = 96 }: TicketPhotoGridProps) {
  const [openIndex, setOpenIndex] = useState<number | null>(null)
  const [expired, setExpired] = useState<Set<string>>(() => new Set())
  if (!attachments || attachments.length === 0) return null

  return (
    <>
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
        {attachments.map((a, i) => (
          <Box
            key={a.id}
            component="button"
            type="button"
            onClick={() => setOpenIndex(i)}
            aria-label={`Open photo ${i + 1}`}
            sx={{
              p: 0,
              border: 'none',
              cursor: 'zoom-in',
              width: size,
              height: size,
              borderRadius: 1,
              overflow: 'hidden',
              bgcolor: 'action.hover',
            }}
          >
            {expired.has(a.id) ? (
              // Said on the tile, not in a hover tooltip a phone never shows.
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 0.25, px: 0.5 }}>
                <BrokenImage color="disabled" />
                <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.1, fontSize: '0.65rem' }}>
                  Link expired, reload
                </Typography>
              </Box>
            ) : (
              <Box
                component="img"
                src={a.thumbnailUrl}
                alt=""
                loading="lazy"
                onError={() => setExpired((prev) => new Set(prev).add(a.id))}
                sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            )}
          </Box>
        ))}
      </Box>

      <TicketPhotoViewer attachments={attachments} index={openIndex} onIndexChange={setOpenIndex} />
    </>
  )
}

interface TicketPhotoViewerProps {
  attachments: TicketAttachmentDto[]
  /** The open photo; null when closed. */
  index: number | null
  onIndexChange: (index: number | null) => void
}

/** Full-size photos, one at a time: arrows, keyboard arrows, or a swipe to step through them. */
function TicketPhotoViewer({ attachments, index, onIndexChange }: TicketPhotoViewerProps) {
  const mobileDialog = useMobileDialogProps()
  const touchStartX = useRef<number | null>(null)
  const count = attachments.length
  const photo = index !== null ? attachments[index] : null
  const close = () => onIndexChange(null)
  const step = (by: number) => {
    if (index === null || count < 2) return
    onIndexChange((index + by + count) % count)
  }

  useEffect(() => {
    if (index === null || count < 2) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') onIndexChange((index - 1 + count) % count)
      else if (e.key === 'ArrowRight') onIndexChange((index + 1) % count)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [index, count, onIndexChange])

  const overlayButton = {
    position: 'absolute',
    bgcolor: 'rgba(0,0,0,0.55)',
    color: '#fff',
    width: 44,
    height: 44,
    zIndex: 1,
    '&:hover': { bgcolor: 'rgba(0,0,0,0.75)' },
  } as const

  return (
    <Dialog
      open={!!photo}
      onClose={close}
      maxWidth="lg"
      {...mobileDialog}
      PaperProps={{ sx: { bgcolor: 'common.black' } }}
    >
      <IconButton aria-label="Close" onClick={close} sx={{ ...overlayButton, top: 8, right: 8 }}>
        <Close />
      </IconButton>
      {count > 1 && (
        <>
          <IconButton aria-label="Previous photo" onClick={() => step(-1)} sx={{ ...overlayButton, left: 8, top: '50%', transform: 'translateY(-50%)' }}>
            <ChevronLeft />
          </IconButton>
          <IconButton aria-label="Next photo" onClick={() => step(1)} sx={{ ...overlayButton, right: 8, top: '50%', transform: 'translateY(-50%)' }}>
            <ChevronRight />
          </IconButton>
          <Typography
            variant="caption"
            sx={{ position: 'absolute', bottom: 'calc(8px + env(safe-area-inset-bottom))', left: 0, right: 0, textAlign: 'center', color: '#fff', zIndex: 1 }}
          >
            {(index ?? 0) + 1} / {count}
          </Typography>
        </>
      )}
      <DialogContent
        sx={{ p: 0, bgcolor: 'common.black', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        onTouchStart={(e) => {
          touchStartX.current = e.touches[0]?.clientX ?? null
        }}
        onTouchEnd={(e) => {
          const start = touchStartX.current
          touchStartX.current = null
          const end = e.changedTouches[0]?.clientX
          if (start === null || end === undefined) return
          // A deliberate horizontal swipe, not a wobble while tapping.
          if (Math.abs(end - start) > 50) step(end < start ? 1 : -1)
        }}
      >
        {photo && (
          <Box
            component="img"
            src={photo.url}
            alt=""
            sx={{ display: 'block', maxWidth: '100%', maxHeight: { xs: '100%', sm: '85vh' }, objectFit: 'contain', mx: 'auto' }}
          />
        )}
      </DialogContent>
    </Dialog>
  )
}
